import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { syncChannel } from "@/lib/broadcast-channels";
import { type CanvasPatch, applyCanvasPatches } from "@/lib/canvas-overlay";
import type { BusinessEntity } from "@/lib/openapi/gen/types.gen";
import { EntityType } from "@/stores/entity-store";
import { useSyncStore } from "@/stores/sync-store";

/**
 * The fields EntitiesDiagram actually draws, and so the only ones worth an
 * indicator on it: names are node labels and relationships are edges, and both
 * appear in buildDiagramSignature. A description save changes nothing on the
 * canvas, so an indicator there would promise a redraw that never comes.
 */
const CANVAS_ATTRIBUTES = ["name", "relationships"];

/**
 * How long an optimistic patch may stand in for server truth before it is
 * dropped regardless.
 *
 * A patch normally lives from the moment a save is queued until the refetch
 * that follows it lands — but every one of those signals is a message that
 * could fail to arrive (the refetch is skipped without a stek, an aborted
 * fetch never completes). A patch left behind would keep showing a value the
 * server may never have accepted, so it expires on its own. Generous on
 * purpose: a save waits out sync-store's 5s inactivity debounce and can then
 * retry twice (1s + 3s) before it either lands or reports failure.
 */
export const OPTIMISTIC_PATCH_MAX_AGE_MS = 30_000;

/**
 * What the canvas shows, and what it says about it, while a save is in flight.
 *
 * Two pieces of state with two different lifetimes, deliberately kept apart:
 *
 *  - `isPending` is the "Saving…" chip: an unsaved change exists. It runs from
 *    the moment a save is queued until sync-store confirms or fails it, which
 *    is exactly what the chip claims. It must NOT wait for the refetch — with
 *    the overlay below, the canvas is already showing the change.
 *
 *  - `patches` is the optimistic overlay: the values the canvas draws in place
 *    of server truth until the server catches up. It outlives `isPending`,
 *    because the entities list is only refetched AFTER the save is confirmed.
 *
 * Both are scoped per entity (not a single flag) because saves on two
 * different objects genuinely overlap — switching objects in
 * DomainObjectDrawer.vue flushes the old one's pending save and starts editing
 * the new one before the old save confirms.
 */
export const useCanvasSaveStatusStore = defineStore("canvasSaveStatus", () => {
    const pendingEntityIds = ref<Record<string, true>>({});
    const patches = ref<Record<string, CanvasPatch>>({});
    /** Saves sync-store has confirmed, whose refetch has not arrived yet. */
    const confirmedEntityIds = ref<Record<string, true>>({});
    const patchTimers: Record<string, ReturnType<typeof setTimeout>> = {};

    const isPending = computed(
        () => Object.keys(pendingEntityIds.value).length > 0
    );

    const dropPatch = (entityId: string) => {
        if (patchTimers[entityId]) {
            clearTimeout(patchTimers[entityId]);
            delete patchTimers[entityId];
        }
        if (entityId in patches.value) {
            const next = { ...patches.value };
            delete next[entityId];
            patches.value = next;
        }
        if (entityId in confirmedEntityIds.value) {
            const next = { ...confirmedEntityIds.value };
            delete next[entityId];
            confirmedEntityIds.value = next;
        }
    };

    const clearPending = (entityId: string) => {
        if (!(entityId in pendingEntityIds.value)) return;
        const next = { ...pendingEntityIds.value };
        delete next[entityId];
        pendingEntityIds.value = next;
    };

    /**
     * Record a queued save: raise the chip, and stand the new values in for
     * server truth on the canvas until the server catches up.
     *
     * `patch` carries only the fields that actually changed, so an edit to one
     * of them never re-asserts a stale value for the other. Merging skips
     * undefined for the same reason.
     */
    const markPending = (entityId: string, patch: CanvasPatch = {}) => {
        pendingEntityIds.value = {
            ...pendingEntityIds.value,
            [entityId]: true,
        };

        const next: CanvasPatch = { ...patches.value[entityId] };
        if (patch.name !== undefined) next.name = patch.name;
        if (patch.relationships !== undefined) {
            next.relationships = patch.relationships;
        }
        if (Object.keys(next).length === 0) return;

        patches.value = { ...patches.value, [entityId]: next };
        // A newer edit supersedes any earlier confirmation: the refetch still
        // in flight carries the PREVIOUS value, and must not be taken as the
        // server's answer to this one.
        if (entityId in confirmedEntityIds.value) {
            const remaining = { ...confirmedEntityIds.value };
            delete remaining[entityId];
            confirmedEntityIds.value = remaining;
        }
        if (patchTimers[entityId]) clearTimeout(patchTimers[entityId]);
        patchTimers[entityId] = setTimeout(
            () => dropPatch(entityId),
            OPTIMISTIC_PATCH_MAX_AGE_MS
        );
    };

    /**
     * Called with each freshly fetched entities list, so patches that have
     * served their purpose let go.
     *
     * The list arriving IS the signal that the refetch completed — the caller
     * watches it by reference, so this runs even when the content came back
     * identical, which is the ordinary case once a guess was right.
     */
    const reconcile = (entities: BusinessEntity[]) => {
        for (const entityId of Object.keys(patches.value)) {
            // The server has answered and this list is the answer, whether or
            // not it agrees with what we guessed. Server truth wins either way.
            if (confirmedEntityIds.value[entityId]) {
                dropPatch(entityId);
                continue;
            }
            // Converged before its own save even landed — the same edit
            // arrived from another tab, or the object left this domain. Tested
            // by identity: applyCanvasPatches hands back the very array it was
            // given when the patch changes nothing.
            const single = { [entityId]: patches.value[entityId] };
            if (applyCanvasPatches(entities, single) === entities) {
                dropPatch(entityId);
            }
        }
    };

    // Confirm path: sync-store posts one "synced" broadcast per changed
    // field of a save, so a message for this entity carrying one of the
    // canvas attributes is the precise, entity-scoped signal that THIS
    // save (not some other entity's, or a field the canvas ignores) has
    // landed — unlike watching business-entities-store's entities list,
    // which changes on every refetch regardless of what caused it.
    //
    // It drops the chip but NOT the patch: the refetch this same message
    // triggers has not run yet, so the list is still a round trip behind.
    //
    // A BroadcastChannel never delivers to the instance that posted it, so
    // a dedicated receiver is needed even within the same tab (same
    // pattern as business-entities-store.ts's own "synced" listener).
    const syncListenerChannel = new BroadcastChannel(syncChannel.name);
    syncListenerChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.entityType === EntityType.BUSINESS_ENTITY &&
                CANVAS_ATTRIBUTES.includes(identity.attribute)
            ) {
                clearPending(identity.entityId);
                if (identity.entityId in patches.value) {
                    confirmedEntityIds.value = {
                        ...confirmedEntityIds.value,
                        [identity.entityId]: true,
                    };
                }
            }
        }
    );

    // Revert path: a save that failed outright is never coming back, so both
    // the indicator and the value it was standing behind go at once — the
    // canvas snaps back to what the server actually holds.
    const syncStore = useSyncStore();
    watch(
        () => syncStore.failedSyncs,
        (failed) => {
            for (const failedSync of failed ?? []) {
                if (
                    failedSync.identity.entityType ===
                        EntityType.BUSINESS_ENTITY &&
                    CANVAS_ATTRIBUTES.includes(failedSync.identity.attribute)
                ) {
                    clearPending(failedSync.identity.entityId);
                    dropPatch(failedSync.identity.entityId);
                }
            }
        }
    );

    return {
        isPending,
        // Exposed as STATE, not behind the `patched()` action: a computed that
        // calls a Pinia action re-evaluates far more often than its inputs
        // change, which makes every object it derives a fresh reference and
        // re-fires identity-keyed watchers downstream. Consumers read this and
        // call `applyCanvasPatches` themselves.
        patches,
        markPending,
        reconcile,
    };
});
